import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isCancellationAccepted, isSettlementInFlight } from '../utils/cancellationSection';
import type { Agreement } from '../types/agreements.types';
import { agreementKeys } from './agreementKeys';
import { useCashPositionWatch } from './useCashPositionWatch';

export const CANCELLATION_POLL_INTERVAL_MS = 3_000;
export const CANCELLATION_POLL_DEADLINE_MS = 30_000;

export type CancellationWatchState = 'idle' | 'watching' | 'timedOut';

export interface CancellationProgressWatch {
    state: CancellationWatchState;
    refresh: () => void;
}

interface WatchTimers {
    poll: ReturnType<typeof setInterval>;
    deadline: ReturnType<typeof setTimeout>;
}

interface HeldState {
    id: string | undefined;
    state: CancellationWatchState;
}

interface Observation {
    id: string;
    settling: boolean;
    accepted: boolean;
}

/** Page-scoped: arms whenever the detail shows settlement in flight, so it survives tab changes and reloads. */
export function useCancellationProgressWatch(
    agreementId: string | undefined,
    agreement: Agreement | undefined,
): CancellationProgressWatch {
    const queryClient = useQueryClient();
    const { startWatch: watchCashPosition } = useCashPositionWatch(agreementId, false);
    const [held, setHeld] = useState<HeldState>({ id: agreementId, state: 'idle' });
    const state = held.id === agreementId ? held.state : 'idle';
    const observed = agreement?.id === agreementId;
    const settling = observed && isSettlementInFlight(agreement);
    const accepted = observed && isCancellationAccepted(agreement);
    const timers = useRef<WatchTimers | null>(null);
    const previous = useRef<Observation | null>(null);

    const stop = useCallback(() => {
        if (timers.current === null) return;
        clearInterval(timers.current.poll);
        clearTimeout(timers.current.deadline);
        timers.current = null;
    }, []);

    const refetchDetail = useCallback(
        (id: string, cancelRefetch = false) =>
            queryClient.refetchQueries({ queryKey: agreementKeys.detail(id), exact: true }, { cancelRefetch }),
        [queryClient],
    );

    const arm = useCallback(
        (id: string) => {
            stop();
            timers.current = {
                poll: setInterval(() => void refetchDetail(id), CANCELLATION_POLL_INTERVAL_MS),
                deadline: setTimeout(() => {
                    stop();
                    setHeld({ id, state: 'timedOut' });
                }, CANCELLATION_POLL_DEADLINE_MS),
            };
            setHeld({ id, state: 'watching' });
        },
        [refetchDetail, stop],
    );

    const settle = useCallback(
        (id: string) => {
            stop();
            setHeld({ id, state: 'idle' });
            void queryClient.invalidateQueries({ queryKey: agreementKeys.history(id) });
            watchCashPosition(null);
        },
        [queryClient, stop, watchCashPosition],
    );

    useEffect(() => {
        if (!agreementId) return;
        if (settling && state === 'idle') arm(agreementId);
        if (!settling && state !== 'idle') settle(agreementId);
    }, [agreementId, settling, state, arm, settle]);

    useEffect(() => {
        if (!agreementId || !observed) return;
        const prior = previous.current?.id === agreementId ? previous.current : null;
        previous.current = { id: agreementId, settling, accepted };
        if (accepted && prior !== null && !prior.accepted && !prior.settling) watchCashPosition(null);
    }, [agreementId, observed, settling, accepted, watchCashPosition]);

    useEffect(() => stop, [agreementId, stop]);

    const refresh = useCallback(() => {
        if (!agreementId) return;
        arm(agreementId);
        void refetchDetail(agreementId, true);
    }, [agreementId, arm, refetchDetail]);

    return { state, refresh };
}
